import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../components/ui/InputField';
import SearchableSelectField from '../../../components/ui/SearchableSelectField';
import OrgDetailSection from './OrgDetailSection';
import { useCountryCodeOptions } from '../../../hooks/useCountryOptions';
import { COST_CENTER_MAX_LENGTH } from '../../../utils/costCenter';
import { LEITWEG_ID_MAX_LENGTH, LeitwegIdProblem } from '../../../utils/leitwegId';

export interface OrgBilling {
    street: string;
    postalCode: string;
    city: string;
    country: string;
    vatId: string;
    billingEmail: string;
    invoiceContact: string;
    /** #1810: the cost centre this organisation's bookings start with. */
    defaultCostCenter: string;
    /** A public body's Leitweg-ID; set, its invoices are XRechnung. */
    leitwegId: string;
}

interface OrgBillingSectionProps {
    value: OrgBilling;
    onChange: (updates: Partial<OrgBilling>) => void;
    /** Mark billing email invalid (e.g. on a save attempt with a bad value). */
    billingEmailError?: boolean;
    /** Mark VAT id invalid (basic format check). */
    vatIdError?: boolean;
    /** Mark country invalid (a stale non-ISO value the picker can't show). */
    countryError?: boolean;
    /** What is wrong with the typed Leitweg-ID, named under the field. */
    leitwegIdError?: LeitwegIdProblem | null;
    /** Render read-only (view mode): inputs disabled, no editing. */
    disabled?: boolean;
}

/**
 * Billing details for a customer organization (plan §4.1). All fields are
 * optional — a small Verein may book without a structured billing address.
 * Mirrors the asset `AddressSection` field-layout pattern but with the
 * billing-specific shape (no house number / coordinates), so the two surfaces
 * stay decoupled.
 */
const OrgBillingSection: React.FC<OrgBillingSectionProps> = ({
    value,
    onChange,
    billingEmailError = false,
    vatIdError = false,
    countryError = false,
    leitwegIdError = null,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const countryCodeOptions = useCountryCodeOptions();

    // The backend stores the ISO 3166-1 alpha-2 code (VARCHAR(2)); the picker
    // constrains input to real codes. An empty leading option keeps the field
    // optional (clearing maps to null in the write payload).
    const countryOptions = React.useMemo(
        () => [
            { value: '', label: t('organization.fields.country_none') },
            ...countryCodeOptions,
        ],
        [countryCodeOptions, t],
    );

    return (
        <OrgDetailSection
            title={t('organization.sections.billing')}
            subtitle={t('organization.sections.billing_subtitle')}
        >
            <div className={'flex flex-col gap-4'}>
                <InputField
                    labelAbove={true}
                    label={t('organization.fields.billing_street')}
                    value={value.street}
                    onChange={(val) => onChange({ street: val })}
                    disabled={disabled}
                />
                <div className={'grid grid-cols-[120px_minmax(0,1fr)] gap-3'}>
                    <InputField
                        labelAbove={true}
                        label={t('organization.fields.postal_code')}
                        value={value.postalCode}
                        onChange={(val) => onChange({ postalCode: val })}
                        disabled={disabled}
                    />
                    <InputField
                        labelAbove={true}
                        label={t('organization.fields.city')}
                        value={value.city}
                        onChange={(val) => onChange({ city: val })}
                        disabled={disabled}
                    />
                </div>
                <div className={'flex flex-col gap-1'}>
                    <label className={'text-sm font-medium text-foreground/80'}>
                        {t('organization.fields.country')}
                    </label>
                    <SearchableSelectField
                        value={value.country.trim().toUpperCase()}
                        onChange={(val) => onChange({ country: val })}
                        options={countryOptions}
                        placeholder={t('organization.fields.country_placeholder')}
                        searchPlaceholder={t('organization.fields.country_search')}
                        emptyMessage={t('organization.fields.country_empty')}
                        disabled={disabled}
                    />
                    {countryError && (
                        <p className={'text-xs text-red-600'}>
                            {t('organization.validation.country_invalid')}
                        </p>
                    )}
                </div>
                <div className={'flex flex-col gap-1'}>
                    <InputField
                        labelAbove={true}
                        label={t('organization.fields.vat_id')}
                        help={t('organization.help.vat_id')}
                        placeholder={t('organization.fields.vat_id_placeholder')}
                        value={value.vatId}
                        onChange={(val) => onChange({ vatId: val })}
                        error={vatIdError}
                        disabled={disabled}
                    />
                    {vatIdError && (
                        <p className={'text-xs text-red-600'}>
                            {t('organization.validation.vat_id_invalid')}
                        </p>
                    )}
                </div>
                <div className={'flex flex-col gap-1'}>
                    <InputField
                        labelAbove={true}
                        label={t('organization.fields.billing_email')}
                        help={t('organization.help.billing_email')}
                        placeholder={t('organization.fields.billing_email_placeholder')}
                        value={value.billingEmail}
                        onChange={(val) => onChange({ billingEmail: val })}
                        error={billingEmailError}
                        disabled={disabled}
                    />
                    {billingEmailError && (
                        <p className={'text-xs text-red-600'}>
                            {t('organization.validation.billing_email_invalid')}
                        </p>
                    )}
                </div>
                <InputField
                    labelAbove={true}
                    label={t('organization.fields.invoice_contact')}
                    placeholder={t('organization.fields.invoice_contact_placeholder')}
                    value={value.invoiceContact}
                    onChange={(val) => onChange({ invoiceContact: val })}
                    disabled={disabled}
                />
                <InputField
                    labelAbove={true}
                    label={t('organization.fields.default_cost_center')}
                    help={t('organization.help.default_cost_center')}
                    placeholder={t('organization.fields.default_cost_center_placeholder')}
                    value={value.defaultCostCenter}
                    onChange={(val) => onChange({ defaultCostCenter: val })}
                    maxLength={COST_CENTER_MAX_LENGTH}
                    disabled={disabled}
                />
                <InputField
                    labelAbove={true}
                    label={t('organization.fields.leitweg_id')}
                    help={t('organization.help.leitweg_id')}
                    placeholder={t('organization.fields.leitweg_id_placeholder')}
                    value={value.leitwegId}
                    onChange={(val) => onChange({ leitwegId: val })}
                    maxLength={LEITWEG_ID_MAX_LENGTH}
                    error={Boolean(leitwegIdError)}
                    errorText={leitwegIdError ? t(`organization.validation.leitweg_id_${leitwegIdError}`) : undefined}
                    disabled={disabled}
                />
            </div>
        </OrgDetailSection>
    );
};

export default OrgBillingSection;
